/**
 * **Episode match reconciliation** — `APPLICATION_SPEC.md` §5.3a.
 *
 * Dishant scoped this on 3 Aug as *"a reconciliation view for stories that don't
 * match"*, and Track A shipped without it: `clickup.match_candidates` held every
 * competing candidate and nothing displayed them.
 *
 * The page exists because the join is fuzzy, and because **an ambiguous match is
 * a plausible-looking wrong answer** — the failure mode this project is most
 * exposed to. Two rules follow from that, and they are the whole design:
 *
 * 1. **It opens on the unresolved rows.** 290 of 349 need nothing.
 * 2. **An ambiguous row shows the tasks it could not choose between, and marks
 *    none of them.** Rendering *"ambiguous"* and then highlighting one would be
 *    resolving the ambiguity in the UI, which is exactly what the schema
 *    refuses to do.
 *
 * Read-only in v1: manual re-pointing needs its own audit trail, and until Q-I5
 * lands nobody knows how often the fuzzy case really occurs.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Pagination, Thumb } from '../components/AssetBits'
import { headCell } from '../components/DataTable'
import { Hint } from '../components/Hint'
import { ManualMatchControl } from '../components/ManualMatch'
import { StatusDot, type StatusState } from '../components/StatusDot'
import { Segmented } from '../components/Tabs'
import { Card, Note, Skeleton, Tag } from '../components/ui'
import { api, type MatchAssetType, type MatchRow } from '../lib/api'
import { ViewHeader } from '../shell/ViewHeader'
import { formatNumber } from '../lib/numbers'
import { formatDate } from '../lib/dates'

/**
 * The chips, named identically to the tiles above them — 16 Sep.
 *
 * They were *Needs a Look · Ambiguous · Unmatched · Not Tracked · Matched* over
 * tiles reading *Structurally Forced · Date-Drift · Ambiguous · Unmatched*, so
 * two of the four tiles matched no chip and one chip matched no tile. Nilay
 * asked what *Needs a Look* was supposed to contain; that is the answer.
 *
 * **Not Tracked → Not logged in ClickUp.** *Tracked* read as something our
 * system failed to do, and it sat next to *No match* — the pair that had to be
 * explained twice in conversation. *Logged* names a step the client's own team
 * takes or does not take, which is what this actually measures.
 *
 * `unresolved` stays as a fifth chip although it is the union of two others: it
 * is the working view and the page's default, and its count now makes the
 * arithmetic visible rather than needing a sentence.
 */
const FILTERS = [
  { key: 'unresolved', label: 'Needs a look' },
  { key: 'ambiguous', label: 'More than one match' },
  { key: 'unmatched', label: 'No match' },
  // Its own chip rather than folded into No match (CU-4). *How much* the client
  // does not log is a real answer about their process, and hiding it would make
  // the gap look like our pipeline missing things.
  { key: 'not_tracked', label: 'Not logged in ClickUp' },
  { key: 'matched', label: 'Matched' },
] as const

type Filter = (typeof FILTERS)[number]['key']

/**
 * Bounded rendering — the lesson `Pagination` already records.
 *
 * Every row carries a thumbnail, and `loading="lazy"` does not save you when
 * all the rows are in the DOM: each one is a redirect plus a signed-URL
 * generation. `/stories` fired **382 requests** and never reached network-idle,
 * which is why that table paginates — and this page repeated the mistake until
 * browser verification timed out on it.
 *
 * 🔴 **The paging is now the server's, and that is the fix rather than a
 * refactor — MT-8.** The endpoint capped every filter at 200 rows and said
 * nothing; this page then sliced *that* into pages of 25, so Matched rendered
 * `Page 1 of 8` over 200 of 766 rows. Abi found it by reading the header, which
 * comes from the uncapped `/summary`, against the table. Slicing a list the
 * client cannot know is complete is the whole defect, so the client no longer
 * holds the list at all.
 */
const PAGE_SIZE = 25

const STATUS: Record<MatchRow['status'], { tone: StatusState; label: string }> = {
  matched: { tone: 'good', label: 'Matched' },
  ambiguous: { tone: 'warning', label: 'More than one match' },
  unmatched: { tone: 'serious', label: 'No match' },
  // Neutral, not a warning. Nobody can act on it and nothing is wrong with it —
  // the client simply does not log this kind of post on this channel.
  not_tracked: { tone: 'idle', label: 'Not logged in ClickUp' },
}

/**
 * 🔴 **`unmatched` is two different findings, and the label said one of them.**
 *
 * Nilay, on a Dead Zone spotlight: *"No Candidate / 2 candidates"* — the status
 * and the two tasks printed next to it flatly contradicting each other.
 *
 * `_assign` writes `unmatched` when **nothing is left**, not when nothing was
 * found: matching is one-to-one, so a task another asset already holds is out
 * of this asset's pool. Measured on the 11 Sep run, 25 of 245 unmatched assets
 * are in that state and every one of their 35 candidate rows was claimed by
 * another asset — 27 of the 35 by the asset published on the task's exact day,
 * which is the matcher's *closer beats further* rule doing exactly its job.
 *
 * So the status is right and only the wording was wrong, and the two readings
 * need different things from the reader:
 *
 * | | means | what helps |
 * |---|---|---|
 * | **No Candidate** | no task within ±1 day | only a Snap story ID on the ClickUp task |
 * | **All Candidates Taken** | every candidate belongs to another asset | nothing here — the tasks shown are someone else's |
 */
function statusOf(row: MatchRow): { tone: StatusState; label: string } {
  if (row.status === 'unmatched' && row.candidate_count > 0) {
    // Plainer than "All Candidates Taken": the reader needs to know the episodes
    // listed below belong to something else, not to learn our word for it.
    return { tone: 'serious', label: 'Already used by another post' }
  }
  return STATUS[row.status]
}

/** The three kinds of asset now sharing this list. Shown as a tag on every row,
 *  because the contrast between them is the most useful thing here: saved
 *  stories resolve to *ambiguous* far more often than spotlights, and that only
 *  reads if they sit in one table. */
const ASSET_TYPES: Record<MatchAssetType, string> = {
  STORY: 'Story',
  SPOTLIGHT: 'Spotlight',
  SAVED_STORY: 'Saved Story',
}

const TYPE_FILTERS = [
  { key: 'all', label: 'All Types' },
  { key: 'STORY', label: 'Stories' },
  { key: 'SPOTLIGHT', label: 'Spotlights' },
  { key: 'SAVED_STORY', label: 'Saved Stories' },
] as const

const cell = { padding: '0.5rem 0.75rem', borderTop: '1px solid var(--border)' } as const

/** `MM/DD/YYYY`, from the one formatter — see `lib/dates.ts`. */
const day = formatDate

/**
 * Renders a list of candidate tasks. **Which** list is `Candidates`' decision.
 *
 * The one rule here is how a *held* task reads. Until 14 Sep every candidate
 * rendered identically, so a task another asset had already claimed was
 * indistinguishable from a free one — which overstates the choice on an
 * ambiguous row: 26 of the 277 tasks listed across the 76 ambiguous assets are
 * already held, and **21 of those rows (28%) show at least one**. Two rows show
 * three tasks of which exactly one is real.
 *
 * So a held task is **dimmed and struck through, never silently dropped**. Since
 * MT-6 it is usually behind the disclosure rather than inline, but when it is on
 * screen it still reads as evidence: it says *this episode's task went to the
 * asset published on its exact day*, which is a better answer than a shorter
 * list with no explanation.
 */
function CandidateList({ items }: { items: MatchRow['candidates'] }) {
  return (
    <ul className="m-0 flex flex-col gap-1" style={{ listStyle: 'none', padding: 0 }}>
      {items.map((candidate) => {
        const taken = candidate.taken_by_asset_id !== null
        return (
          <li key={candidate.task_id} className="text-xs">
            <span
              style={{
                color: taken
                  ? 'var(--text-label)'
                  : candidate.chosen
                    ? 'var(--text-primary)'
                    : 'var(--text-secondary)',
                fontWeight: candidate.chosen ? 600 : 400,
                textDecoration: taken ? 'line-through' : undefined,
              }}
            >
              {candidate.name ?? candidate.task_id}
            </span>
            <span className="ml-2" style={{ color: 'var(--text-label)' }}>
              {candidate.episode_number ? `#${candidate.episode_number} · ` : ''}
              {candidate.author ?? 'no author'} · {day(candidate.publish_date_local)}
              {candidate.day_offset !== 0 && (
                <>
                  {' '}
                  {/* Not a warning once the task is gone: the offset explains
                      the loss rather than flagging a weak match to weigh up. */}
                  <Tag tone={taken ? 'neutral' : 'warning'}>
                    {candidate.day_offset > 0 ? '+' : ''}
                    {candidate.day_offset}d
                  </Tag>
                </>
              )}
            </span>
            {candidate.chosen && (
              <span className="ml-2">
                <Tag tone="brand">Chosen</Tag>
              </span>
            )}
            {taken && (
              // Named by what the reader can see on this screen — the kind of
              // asset and the day it went out. The asset id is the precise
              // answer and means nothing to anyone reading this page.
              <span className="ml-2" style={{ color: 'var(--text-label)' }}>
                taken by the{' '}
                {candidate.taken_by_asset_type
                  ? ASSET_TYPES[candidate.taken_by_asset_type].toLowerCase()
                  : 'asset'}
                {candidate.taken_by_publish_date
                  ? ` published ${day(candidate.taken_by_publish_date)}`
                  : ''}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The tasks this asset **did not get**, behind a disclosure — MT-6.
 *
 * Abi, 15 Sep: *"can we have the items that were not chosen show up in some sort
 * of additional dropdown so that the one chosen is the only one they see at
 * first glance? this view feels very overwhelming."*
 *
 * One rule decides what collapses: **the disclosure holds the tasks this asset
 * did not get.** Anything still in play stays on screen.
 *
 * | Row status | Always visible | Collapsed |
 * |---|---|---|
 * | **matched** | the chosen task | every other candidate |
 * | **ambiguous** | every *free* candidate, none marked | only tasks another asset holds |
 * | **unmatched**, all taken | nothing — the status line says it | all of them |
 *
 * 🔴 **The ambiguous case cannot collapse to "the one chosen", because there is
 * no chosen one.** This screen exists so an ambiguous row shows the tasks it
 * could not choose between and **marks none of them** (module note above, spec
 * §5.3a). Applying the ask literally there would either invent a choice or hide
 * the evidence for it. Collapsing the *taken* tasks instead cuts the same
 * clutter without resolving anything — 26 of the 277 tasks listed across the 76
 * ambiguous assets are already held, and a task that is not on offer is not part
 * of the decision.
 *
 * The tab Abi was reading is **Matched**, where the rule does what she asked:
 * one line per row.
 */
function Candidates({ row }: { row: MatchRow }) {
  const [open, setOpen] = useState(false)

  // ST-28: a linked saved story never had date candidates for the task it holds —
  // it took its public story's. Saying "No episode within 1 day" beside
  // Matched contradicted itself on 74 rows, so the evidence it does have is shown.
  if (row.match_source === 'linked' && row.linked_story) {
    return <LinkedEvidence row={row} />
  }
  // A matched row whose task is not among its own candidates (a manual pin can
  // be) still says which task it got, rather than only who lost.
  const unlisted =
    row.status === 'matched' &&
    row.chosen_task !== null &&
    !row.candidates.some((candidate) => candidate.chosen)
  if (unlisted && row.chosen_task) {
    return <ChosenTaskLine task={row.chosen_task} />
  }

  if (!row.candidates.length) {
    return (
      <span className="text-xs" style={{ color: 'var(--text-label)' }}>
        No episode within {row.tolerance_days ?? 1} day of publishing
      </span>
    )
  }

  // A task another asset holds is not on offer here, whatever the status. On a
  // matched row the chosen task is the answer and the rest are working.
  const shown = row.candidates.filter((candidate) =>
    row.status === 'matched' ? candidate.chosen : candidate.taken_by_asset_id === null,
  )
  const hidden = row.candidates.filter((candidate) => !shown.includes(candidate))

  return (
    <div className="flex flex-col gap-1">
      {shown.length > 0 && <CandidateList items={shown} />}
      {hidden.length > 0 && (
        <>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="text-xs"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--brand-ink)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {open ? '▾ ' : '▸ '}
            {/* Two different facts, so two different sentences. On a matched row
                the hidden tasks are the ones that lost; elsewhere every hidden
                task is held by another asset, which is *why* it is not an option
                — and that is the more useful thing to say. */}
            {row.status === 'matched'
              ? `${hidden.length} other${hidden.length === 1 ? '' : 's'} considered`
              : `${hidden.length} already used elsewhere`}
          </button>
          {open && <CandidateList items={hidden} />}
        </>
      )}
    </div>
  )
}

function ChosenTaskLine({ task }: { task: NonNullable<MatchRow['chosen_task']> }) {
  return (
    <span className="block text-xs" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
      {task.name ?? task.task_id}
      <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
        {' · '}
        {task.episode_number ? `#${task.episode_number} · ` : ''}
        {task.author ?? 'no author'}
      </span>
    </span>
  )
}

/**
 * **ST-28 — why a saved story has a task with no date candidates.** It shares its
 * videos with the public story it was saved from, and took that story's task.
 * The snap count is the evidence a reader can weigh: 45 of 45 is the same story;
 * the link rule needs at least half.
 */
function LinkedEvidence({ row }: { row: MatchRow }) {
  const link = row.linked_story!
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
        Through its public story published {day(link.published_at?.slice(0, 10) ?? null)} · same
        videos, {link.snaps_matched} of {link.snaps_total} snaps
      </span>
      {row.chosen_task && <ChosenTaskLine task={row.chosen_task} />}
    </div>
  )
}

/**
 * How many candidates, **and how many of them this asset could actually have**.
 *
 * Two changes from the bare count it replaces. It now renders at one candidate
 * as well as two — a single-candidate `unmatched` row previously showed a
 * status of *No Candidate* beside one printed task and no count at all, which
 * is the same contradiction with nothing to explain it. And when a task is held
 * elsewhere it says so, because `candidate_count` includes tasks that are not
 * on offer: *3 candidates* can mean one real option.
 */
function CandidateCount({ row }: { row: MatchRow }) {
  const { candidate_count: total, free_candidate_count: free } = row
  // A linked row's own date candidates did not decide it (ST-28).
  if (total === 0 || row.match_source === 'linked') return null

  // A matched row is answered, so what is *available* is no longer the
  // question — and "1 of 2 still free" would describe the chosen task itself
  // as free, inviting a choice nobody has to make. The taken competitor is
  // still struck through in the list beside it.
  if (row.status === 'matched') {
    return (
      <span className="block text-xs" style={{ color: 'var(--text-label)' }}>
        {total} possible episode{total === 1 ? '' : 's'}
      </span>
    )
  }

  const label =
    free === total
      ? `${total} possible episode${total === 1 ? '' : 's'}`
      : free === 0
        ? total === 1
          ? '1 possible episode, already used'
          : `${total} possible episodes, all already used`
        : `${free} of ${total} still available`

  return (
    <span className="block text-xs" style={{ color: 'var(--text-label)' }}>
      {label}
    </span>
  )
}

export function Matches() {
  const [filter, setFilter] = useState<Filter>('unresolved')
  const [type, setType] = useState<(typeof TYPE_FILTERS)[number]['key']>('all')
  const [page, setPage] = useState(0)
  const summary = useQuery({ queryKey: ['match-summary'], queryFn: api.matchSummary })
  const rows = useQuery({
    queryKey: ['matches', filter, type, page],
    queryFn: () =>
      api.matches(filter, type === 'all' ? null : type, { page, pageSize: PAGE_SIZE }),
    /**
     * Keeps the current page on screen while the *next page* loads — but only
     * then.
     *
     * 🔴 **Not a blanket `(previous) => previous`.** That also holds rows across
     * a *filter* change, so clicking **Matched** would render the previous tab's
     * ambiguous rows underneath the Matched chip until the request returned.
     * Rows presented under a heading they do not belong to is the exact failure
     * this screen exists to prevent — a plausible-looking wrong answer — and it
     * would be worth far more than the flicker it saves.
     *
     * Stepping pages inside one filter is the safe case: same question, next
     * slice. So the previous data is reused only when the filter and the asset
     * type are unchanged.
     */
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey as
        | [string, Filter, (typeof TYPE_FILTERS)[number]['key'], number]
        | undefined
      return key && key[1] === filter && key[2] === type ? previous : undefined
    },
  })

  const visible = rows.data?.rows ?? []
  const rowCount = rows.data?.row_count ?? 0
  const pageCount = Math.max(Math.ceil(rowCount / PAGE_SIZE), 1)
  // Clamped, and it must also *correct* the request: a stale `?page=` in a
  // shared link, or switching from 711 matched rows to 76 ambiguous ones while
  // on page 9, would otherwise ask the server for an offset past the end and
  // render an empty table under a valid-looking pager.
  const current = Math.min(page, pageCount - 1)
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1)
  }, [page, pageCount])

  const total = summary.data?.total ?? 0
  const unresolved = (summary.data?.ambiguous ?? 0) + (summary.data?.unmatched ?? 0)

  /**
   * **MT-9 — the chips carry their counts.**
   *
   * Abi, 15 Sep, of *Needs a Look*: *"what is supposed to show up here? this
   * doesn't match any of the KPI categories."* It is `ambiguous + unmatched` and
   * nothing on screen said so — the four tiles read Structurally Forced ·
   * Date-Drift · Ambiguous · Unmatched, and the five chips read Needs a Look ·
   * Ambiguous · Unmatched · Not Tracked · Matched.
   *
   * The counts make the arithmetic self-evident: 33 + 26 = 59 is visible without
   * being explained, and the chips now reconcile against the tiles and the
   * header line in one glance. Cheaper and more durable than a sentence.
   *
   * Keyed by filter and read from the same `/summary` response the tiles use, so
   * the two cannot drift apart.
   */
  const counts: Partial<Record<Filter, number>> = summary.data
    ? {
        unresolved,
        ambiguous: summary.data.ambiguous,
        unmatched: summary.data.unmatched,
        not_tracked: summary.data.not_tracked,
        matched: summary.data.matched,
      }
    : {}

  return (
    <div>
      <ViewHeader
        title="Episode Matches"
        status={
          summary.data
            ? `${formatNumber(summary.data.matched)} of ${formatNumber(total)} matched to an episode · ${formatNumber(unresolved)} need a look`
            : undefined
        }
        scoped={false}
      />

      {/* **No intro paragraph — removed 11 Sep on Nilay's instruction.**
          It explained where the three ClickUp fields come from and that only the
          three English channels carry spotlight and saved-story tasks. The
          screen says both without prose: the header counts matched against
          total, and *Not Tracked* is a filter chip with its own count, which is
          a better home for a coverage fact than a sentence above it.

          The *Back to Connections* link went with the paragraph it sat in.
          `Connections` is in the sidebar, so this is not a dead end. */}

      {summary.data && (
        <div
          className="mb-4 grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(11rem, 100%), 1fr))' }}
        >
          {/**
            * 🔴 **Four tiles that partition the total — rebuilt 16 Sep.**
            *
            * The old four were *Structurally Forced · Date-Drift · Ambiguous ·
            * Unmatched*, and they were two different axes in one row: the first
            * two are subdivisions of *matched*, the last two are peer statuses,
            * and **`not_tracked` appeared in no tile at all**. So they summed to
            * 1,102 against a stated total of 1,271 — 169 posts simply absent —
            * and *Structurally Forced 678* next to *Matched 780* in the chips
            * below read as a contradiction. Nilay: *"on Structurally Forced
            * shows 678 but matched shows 780 — why so?"*
            *
            * Now every post lands in exactly one tile and the four sum to the
            * total the header states, so the reader can check the arithmetic
            * instead of trusting it.
            *
            * 🔴 **`Structurally Forced` is gone, and it was also wrong.** It was
            * described as *"matched on the exact day, with no other possible
            * pairing"* while the query behind it tested only `day_offset = 0` —
            * it never tested whether other pairings existed. **273 of its 678
            * rows had competing tasks.** The honest part of it, *how many
            * matches leaned on a ±1 day inference*, survives as a caveat inside
            * Matched, which is where a reason to doubt a number belongs.
            */}
          {[
            {
              label: 'Matched',
              value: summary.data.matched,
              note: [
                'Episode, writer and re-run are showing.',
                summary.data.drift_matched > 0 &&
                  `${formatNumber(summary.data.drift_matched)} matched a day either side.`,
                // ST-28: without this, Matched jumps by ~116 with nothing to say why.
                summary.data.matched_linked > 0 &&
                  `${formatNumber(summary.data.matched_linked)} saved ${
                    summary.data.matched_linked === 1 ? 'story' : 'stories'
                  } matched through the public story ${
                    summary.data.matched_linked === 1 ? 'it was' : 'they were'
                  } saved from.`,
              ]
                .filter(Boolean)
                .join(' '),
            },
            {
              label: 'More than one match',
              value: summary.data.ambiguous,
              note: 'Several episodes fit the date, so none is shown.',
            },
            {
              label: 'No match',
              value: summary.data.unmatched,
              note: 'No episode found within a day of publishing.',
            },
            {
              // **Not a failure, and the wording has to carry that.** These are
              // posts the client never logs in ClickUp — 110 of them spotlights
              // on the three channels that have no spotlight tasks at all. Left
              // unexplained, 164 reads as 164 things we broke.
              label: 'Not logged in ClickUp',
              value: summary.data.not_tracked,
              note: 'No task was ever created for these, so there is nothing to match.',
            },
          ].map((tile) => (
            <div
              key={tile.label}
              className="px-3 py-2"
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div className="text-xs" style={{ color: 'var(--text-label)' }}>
                {tile.label}
              </div>
              <div className="is-num text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {formatNumber(tile.value)}
              </div>
              <div className="mt-1 text-xs" style={{ color: 'var(--text-label)' }}>
                {tile.note}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        <Segmented
          options={FILTERS.map((entry) => ({
            ...entry,
            label:
              counts[entry.key] === undefined
                ? entry.label
                : `${entry.label} ${formatNumber(counts[entry.key]!)}`,
          }))}
          active={filter}
          onChange={(next) => {
            setFilter(next)
            setPage(0)
          }}
          label="Show"
        />
        <Segmented
          options={TYPE_FILTERS}
          active={type}
          onChange={(next) => {
            setType(next)
            setPage(0)
          }}
          label="Asset type"
        />
      </div>

      <Card
        title="Assets and Their Candidate Tasks"
      >
        {rows.isLoading ? (
          <Skeleton rows={5} />
        ) : rows.error ? (
          <Note tone="error">Could not load matches.</Note>
        ) : !visible.length ? (
          <Note tone={filter === 'unresolved' ? 'good' : 'info'}>
            {filter === 'unresolved'
              ? total === 0
                ? 'No stories have been matched yet — the ClickUp pipeline has not run against this database.'
                : 'Every story resolved to exactly one task, so nothing needs a look.'
              : 'No stories in this state.'}
          </Note>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <caption className="sr-only">
Snapchat posts with their match status and the ClickUp episodes that could fit
              </caption>
              <thead>
                <tr>
                  <th scope="col" style={headCell} />
                  <th scope="col" style={headCell}>Type</th>
                  <th scope="col" style={headCell}>Channel</th>
                  <th scope="col" style={headCell}>Published</th>
                  <th scope="col" style={headCell}>Status</th>
                  <th scope="col" style={headCell}>Possible episodes</th>
                  {/* §5.3a lists both: without them a reader cannot tell whether
                      a row reflects the current rule or a stale evaluation. */}
                  {/* One quiet column, not two. `±1d · UTC · rule 2` beside a
                      separate `Evaluated` date put pipeline settings on every
                      row of a screen the client reads. §5.3a wants both facts
                      recorded — without them a row cannot be told from a stale
                      evaluation — so they are kept, together, on hover. */}
                  <th scope="col" style={headCell}>Checked</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.asset_id}>
                    <td style={{ ...cell, width: '2rem' }}>
                      <Thumb
                        assetId={row.asset_id}
                        has={row.has_thumbnail}
                        alt={`Thumbnail for the Story Published ${day(row.publish_date_local)}`}
                      />
                    </td>
                    <td style={cell}>
                      <Tag tone="neutral">{ASSET_TYPES[row.asset_type]}</Tag>
                    </td>
                    <th scope="row" style={{ ...cell, textAlign: 'left', fontWeight: 500 }}>
                      {row.display_label}
                    </th>
                    <td style={{ ...cell }} className="is-num text-xs">
                      {day(row.publish_date_local)}
                      {row.published_at && (
                        <span className="block" style={{ color: 'var(--text-label)' }}>
                          {new Date(row.published_at).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'UTC',
                          })}{' '}
                          UTC
                        </span>
                      )}
                    </td>
                    <td style={cell}>
                      <StatusDot state={statusOf(row).tone}>{statusOf(row).label}</StatusDot>
                      <CandidateCount row={row} />
                      {/* MT-17: where an unmatched or ambiguous post gets its task by hand. */}
                      <div>
                        <ManualMatchControl target={row} />
                      </div>
                    </td>
                    <td style={cell}>
                      <Candidates row={row} />
                    </td>
                    <td style={{ ...cell }} className="is-num text-xs">
                      {/* An evaluated-and-found-nothing row MUST carry when it
                          was checked, or it is indistinguishable from one that
                          was never checked — the distinction §5.3a exists for.
                          The rule that produced it rides along in the tooltip,
                          where it is still recorded but is not on screen for a
                          reader who came to find a missing episode. */}
                      {row.evaluated_at ? (
                        // `Hint`, not `title` — the app's own tooltip. The
                        // native one waits ~1s and renders outside the token
                        // system, which is why every other tooltip here is a
                        // Hint and why this column's detail was unreadable.
                        <Hint
                          label={
                            <>
                              Checked {day(row.evaluated_at)}
                              <br />
                              Matched within {row.tolerance_days ?? 1} day
                              {row.match_tz ? `, dates read in ${row.match_tz}` : ''}
                              {row.rule_version ? ` · rule ${row.rule_version}` : ''}
                            </>
                          }
                        >
                          <span>{day(row.evaluated_at)}</span>
                        </Hint>
                      ) : (
                        <span style={{ color: 'var(--text-label)' }}>never</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rowCount > 0 && (
          <Pagination
            page={current}
            pageCount={pageCount}
            // The total for this filter, not the length of what is rendered —
            // they were the same number only while the list was unpaged.
            rowCount={rowCount}
            onChange={setPage}
          />
        )}
      </Card>

      {/* **MT-4 — the closing blurb is gone.** Abi, 15 Sep: *"what is the
          purpose of the text blurb at the bottom? It looks AI generated and an
          explainer of the rules we're following but not particularly useful to
          the client."* Right about the audience: it argued a case the reader
          cannot act on. Nothing is lost — it is in
          `docs/CLIENT-ANSWERS-2026-09-11.md` §3, the document that reaches Jesse.

          **MT-5** puts in its place the one fact a reader of this screen needs
          and previously had to infer from an absence: these rows cannot be
          edited here. Manual re-pointing needs its own audit trail and has to
          survive the nightly matcher re-running, so it is deliberately not
          built (spec §5.3a). */}
      <Note>
        Matches run nightly and can&rsquo;t be edited here. Adding the Snap story link
        to each ClickUp task would remove the guesswork.
      </Note>
    </div>
  )
}
